import InfiniteScroll from 'react-infinite-scroll-component';
import { useSocket } from '../../global/socket';
import { useCallback, useMemo, useState } from 'react';
import { ApiGame } from '../../../src/types/apigame';
import { ClientRequests } from '../../../src/enum/socket';
import { GetGamesForUserParams, GetGamesForUserType } from '../../../src/models/socket/game';
import { Spinner } from '@blueprintjs/core';
import { useSession } from 'next-auth/react';
import GamesListGame from './gameslistgame';

export const COMPLETED_GAMES_LIST_DEFAULT_PAGE_SIZE = 20;

type Props = {
    pageSize?: number;
    initialData: ApiGame[];
};

export default function CompletedGamesList(
    {
        initialData,
        pageSize = COMPLETED_GAMES_LIST_DEFAULT_PAGE_SIZE,
    }: Props,
): JSX.Element {
    const { socket } = useSocket();
    const { data: session } = useSession();
    const [games, setGames] = useState<ApiGame[] | null>(initialData);

    const userId = Number(session.user['id']);

    const getGames = useCallback(
        async (
            cursor?: number,
        ): Promise<ApiGame[] | null> => {
            if (!socket) {
                return;
            }

            const params: GetGamesForUserParams = {
                searchType: GetGamesForUserType.Completed,
                take: pageSize,
                cursor,
            };

            const newGames = await socket.emitWithAck(
                ClientRequests.GetGames,
                params,
            );

            return newGames;
        },
        [socket, pageSize],
    );

    const nextPage = useCallback(
        async (): Promise<void> => {
            if (!socket || games.length === 0) {
                return;
            }

            const newGames = await getGames(
                games[games.length - 1].id,
            );

            setGames([...games, ...newGames]);
        },
        [getGames, socket, setGames, games],
    )

    const items = useMemo(
        () => games.map(
            (game) => <GamesListGame key={game.id} game={game} userId={userId} />
        ),
        [games, userId],
    );

    return <InfiniteScroll
        dataLength={games.length}
        next={nextPage}
        hasMore={games && games.length > 0 && (games.length % pageSize) === 0}
        loader={<Spinner />}
        style={{ overflow: 'visible', minWidth: '640px' }}
    >
        {items}
    </InfiniteScroll>;
}
